import { useEffect, useRef } from 'react';

type Options = {
  /** Fully-built ws(s):// URL incl. ?token=. When null/empty the socket stays closed. */
  url: string | null;
  /** Called for every parsed JSON message frame. */
  onMessage: (data: unknown) => void;
  /** Called once each time the socket (re)opens — use to sync via a single refetch. */
  onOpen?: () => void;
  /** Close codes on which we stop retrying (auth failures). */
  stopRetryCodes?: number[];
};

const BASE_DELAY_MS = 1_000;
const MAX_DELAY_MS = 30_000;

/**
 * Receive-only WebSocket with exponential backoff reconnect.
 *
 * - Reconnects with backoff capped at ~30s; the delay resets to base on every
 *   successful open.
 * - Stops retrying when the server closes with one of `stopRetryCodes`.
 * - Tears down cleanly on unmount or whenever `url` changes (e.g. switching
 *   conversations), so no stale socket leaks.
 *
 * Callbacks are read through a ref so changing inline handlers does NOT force a
 * reconnect — only `url` does.
 */
export const useReconnectingSocket = ({
  url,
  onMessage,
  onOpen,
  stopRetryCodes = [],
}: Options): void => {
  const handlersRef = useRef({ onMessage, onOpen });
  handlersRef.current = { onMessage, onOpen };

  const stopCodesRef = useRef(stopRetryCodes);
  stopCodesRef.current = stopRetryCodes;

  useEffect(() => {
    if (!url) return;

    let socket: WebSocket | null = null;
    let retryTimer: number | undefined;
    let attempt = 0;
    let disposed = false;

    const connect = (): void => {
      if (disposed) return;
      const ws = new WebSocket(url);
      socket = ws;

      ws.onopen = () => {
        attempt = 0;
        handlersRef.current.onOpen?.();
      };

      ws.onmessage = (event) => {
        try {
          handlersRef.current.onMessage(JSON.parse(event.data));
        } catch {
          // ignore non-JSON / malformed frames
        }
      };

      ws.onclose = (event) => {
        if (disposed) return;
        if (stopCodesRef.current.includes(event.code)) return;
        const delay = Math.min(BASE_DELAY_MS * 2 ** attempt, MAX_DELAY_MS);
        attempt += 1;
        retryTimer = window.setTimeout(connect, delay);
      };

      ws.onerror = () => {
        // onclose will follow and drive the reconnect.
      };
    };

    connect();

    return () => {
      disposed = true;
      if (retryTimer) window.clearTimeout(retryTimer);
      if (socket) {
        socket.onopen = null;
        socket.onmessage = null;
        socket.onclose = null;
        socket.onerror = null;
        socket.close();
      }
    };
  }, [url]);
};
